import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePrivate from '@afosto/auth-react/usePrivate';
import useStripe from '@afosto/utils-react/stripe/useStripe';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { Elements, injectStripe, StripeProvider } from 'react-stripe-elements';
import { AnimatePresence, motion } from 'framer-motion';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import CircularProgress from '@material-ui/core/CircularProgress';
import ConfirmationSection from './ConfirmationSection';
import AccountNameSection from './AccountNameSection';
import FormActions from '@afosto/app-components-react/Form/FormActions';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import Page from '@afosto/app-components-react/Page';
import StripeIdealBankField from '../../components/StripeComponents/StripeIdealBankField';
import createIdealSource from '../../utils/stripe/createIdealSource';
import parseUrlQuery from '@afosto/utils/parseUrlQuery';
import translations from './translations';
import styled from 'styled-components';

const stripeApiKey = process.env.REACT_APP_STRIPE_API_KEY;

const DURATION = 0.1;

const TRANSITIONS = {
  initial: {
    opacity: 0,
    transform: 'translateY(5px)',
  },
  initialUp: {
    opacity: 0,
    transform: 'translateY(-5px)',
  },
  enter: {
    opacity: 1,
    transform: 'translateY(0px)',
    transition: {
      duration: DURATION,
      when: 'beforeChildren',
      ease: 'easeOut',
    },
  },
  exit: {
    opacity: 0,
    transform: 'translateY(5px)',
    transition: {
      duration: DURATION,
      ease: 'easeOut',
    },
  },
  exitUp: {
    opacity: 0,
    transform: 'translateY(-5px)',
    transition: {
      duration: DURATION,
      ease: 'easeOut',
    },
  },
};

const InjectedIdealField = injectStripe(StripeIdealBankField, { withRef: true });
const CardOverflow = styled(Card)`
  && {
    overflow: visible;
  }
`;

const IdealCallback = ({ location, stripe }) => {
  usePrivate();

  const intl = useIntl();
  const [debitSource, setDebitSource] = useState(null);
  const [currentStep, setCurrentStep] = useState('confirmed');
  const [isRedirecting, setIsRedirecting] = useState(false);

  const redirectToIdeal = async () => {
    setIsRedirecting(true);

    try {
      const { source = {} } = await createIdealSource(stripe, { bank: debitSource.ideal.bank });
      const { redirect } = source;
      const { url } = redirect;

      window.location.href = url;

      return url;
    } catch (error) {
      setIsRedirecting(false);
      return Promise.reject(error);
    }
  };

  useEffect(() => {
    const fetchStripeSource = async (sourceID, secret) => {
      try {
        const { source } = await stripe.retrieveSource({ id: sourceID, client_secret: secret });
        setDebitSource(source);
        return source;
      } catch (error) {
        return Promise.reject(error);
      }
    };

    const { client_secret: clientSecret, source } = parseUrlQuery(location.search);

    if (clientSecret && source && stripe) {
      fetchStripeSource(source, clientSecret);
    }
  }, [location.search, stripe]);

  if (!debitSource) {
    return (
      <Page titleHidden>
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="center" pt={4} pb={4}>
              <CircularProgress />
            </Box>
          </CardContent>
        </Card>
      </Page>
    );
  }

  const hasPaymentFailed = debitSource && debitSource.status === 'failed';

  if (hasPaymentFailed) {
    return (
      <Page titleHidden>
        <CardOverflow>
          <LoadingWrapper isLoading={isRedirecting}>
            <CardHeader
              title={intl.formatMessage(translations.paymentFailed.title)}
              titleTypographyProps={{ variant: 'h6' }}
            />
            <CardContent>
              <FormattedMessage {...translations.paymentFailed.description} />
              <InjectedIdealField value={debitSource.ideal.bank} />
              <FormActions>
                <Button variant="contained" color="primary" onClick={redirectToIdeal}>
                  <FormattedMessage {...translations.actions.retry} />
                </Button>
                <Button variant="outlined" component={Link} to="/contract/billing">
                  <FormattedMessage {...translations.actions.cancel} />
                </Button>
              </FormActions>
            </CardContent>
          </LoadingWrapper>
        </CardOverflow>
      </Page>
    );
  }

  return (
    <Page title={intl.formatMessage(translations.title)} titleHidden>
      <Card>
        <AnimatePresence exitBeforeEnter initial={false}>
          {currentStep === 'confirmed' && (
            <motion.div
              key="confirmed"
              variants={TRANSITIONS}
              initial="initialUp"
              animate="enter"
              exit="exitUp"
            >
              <ConfirmationSection debitSource={debitSource} setCurrentStep={setCurrentStep} />
            </motion.div>
          )}
          {currentStep === 'accountName' && (
            <motion.div
              key="accountName"
              variants={TRANSITIONS}
              initial="initial"
              animate="enter"
              exit="exit"
            >
              <AccountNameSection
                setCurrentStep={setCurrentStep}
                debitSource={debitSource}
                stripe={stripe}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </Card>
    </Page>
  );
};

IdealCallback.propTypes = {
  location: PropTypes.object,
  stripe: PropTypes.object,
};

IdealCallback.defaultProps = {
  location: undefined,
  stripe: undefined,
};

const InjectedPaymentCallback = injectStripe(IdealCallback);

const PaymentCallbackInjected = props => {
  const [stripe] = useStripe(stripeApiKey);
  return (
    <StripeProvider stripe={stripe}>
      <Elements>
        <InjectedPaymentCallback {...props} />
      </Elements>
    </StripeProvider>
  );
};

export default PaymentCallbackInjected;
